<script lang="ts">
	// UC-19. The owner's sales report, as the Owner Dashboard - Sales page draws it: a
	// period and a shop held at the top, and one part of the report at a time
	// below them, Overview, Items, Hours or Discounts, because one page with
	// every block was too crowded to read.
	//
	// It only reads. What each figure is for, and why Discounts lists each
	// cashier's discounts beside their orders, is argued in the plan's UC-19,
	// which builds no sales by cashier and no comparison of shops. The rows
	// come in the server's order: the largest amount first.
	//
	// The presets become dates here, counted back from Today. Today is the
	// trading day the server gives the chosen shop's day (the latest one
	// for all shops), so it agrees with Home and the till. Whether a range is
	// allowed is the server's to say.

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Meter from '../components/Meter.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import type { components } from '../api/types';
	import { fill, message, type Language, type MessageKey } from '../i18n/messages';
	import { leftToRight } from '../format/bidi';
	import { formatMinor } from '../format/money';
	import type { Account } from '../owner/session.svelte';
	import { loadDays, ownerShops, ownerDays } from '../owner/data.svelte';
	import {
		byTradingHour,
		daySpan,
		formatDay,
		formatRange,
		hourLabel,
		itemRowId,
		itemRowKey,
		periods,
		presetRange,
		type Period,
		withBars,
	} from '../owner/reports';

	type Report = components['schemas']['SalesReportBody'];
	type Item = components['schemas']['ItemSalesBody'];
	type Discount = components['schemas']['ReportDiscountBody'];
	type TableRow = components['schemas']['ReportTableBody'];
	type Part = 'overview' | 'items' | 'hours' | 'discounts' | 'tables';

	interface Props {
		language: Language;
		account: Account;
		shopId?: string;
	}

	let { language, account, shopId = '' }: Props = $props();

	const minorDigits = $derived(account.settings.minorDigits);
	const money = (minor: number) => formatMinor(minor, minorDigits);
	const shops = $derived(ownerShops());
	const days = $derived(ownerDays());

	$effect(() => {
		if (shops.loaded) void loadDays();
	});

	let period = $state<Period>('today');
	let selectedShop = $state('all');
	const chosenShop = $derived(shopId || selectedShop);
	let part = $state<Part>('overview');
	// The range's two days, as the owner typed them. Empty until a range is
	// picked, when they start from the period on screen.
	let rangeFrom = $state('');
	let rangeTo = $state('');

	const periodKeys: Record<Period, MessageKey> = {
		today: 'reportsToday',
		yesterday: 'reportsYesterday',
		last7: 'reportsLast7',
		month: 'reportsMonth',
		lastMonth: 'reportsLastMonth',
		range: 'reportsRange',
	};

	const today = $derived.by(() => {
		const dates = shops.value
			.filter((shop) => chosenShop === 'all' || shop.id === chosenShop)
			.map((shop) => days.days[shop.id]?.summary.tradingDay)
			.filter((date): date is string => Boolean(date))
			.sort();
		return dates.at(-1) ?? '';
	});

	// The hour a trading day starts: the chosen shop's, or the earliest of
	// the shops', which is where the server begins the period too.
	const dayStartHour = $derived.by(() => {
		const hours = shops.value
			.filter((shop) => chosenShop === 'all' || shop.id === chosenShop)
			.map((shop) => shop.dayStartHour);
		return hours.length ? Math.min(...hours) : 0;
	});

	const range = $derived(
		period === 'range' ? { from: rangeFrom, to: rangeTo } : presetRange(period, today),
	);
	const complete = $derived(Boolean(today && range.from && range.to));

	function pickPeriod(next: Period) {
		if (next === 'range' && period !== 'range') {
			rangeFrom = range.from;
			rangeTo = range.to;
		}
		period = next;
	}

	let report = $state<Report | null>(null);
	let rangeError = $state<MessageKey | null>(null);
	let failed = $state(false);
	let pending = $state(false);
	// A wait shows only after 300 ms; until then the last report stays up.
	let waitShown = $state(false);
	let asked = 0;

	$effect(() => {
		if (!complete) return;
		const query = {
			from: range.from,
			to: range.to,
			shopId: chosenShop === 'all' ? undefined : chosenShop,
			// This bundle reads weighed items' portions (UC-19.R13).
			weighed: true,
		};
		const ask = ++asked;
		pending = true;
		const timer = setTimeout(() => {
			if (ask === asked) waitShown = true;
		}, 300);
		void (async () => {
			let next: Report | null = null;
			let problem: MessageKey | null = null;
			let broke = false;
			try {
				const { data, error } = await api.GET('/api/owner/reports/sales', { params: { query } });
				if (data) next = data;
				else if (error?.code === 'RANGE_INVALID') problem = 'reportsInvalid';
				else if (error?.code === 'RANGE_TOO_LONG') problem = 'reportsTooLong';
				else broke = true;
			} catch {
				broke = true;
			}
			clearTimeout(timer);
			if (ask !== asked) return;
			report = next;
			rangeError = problem;
			failed = broke;
			pending = false;
			waitShown = false;
		})();
		return () => clearTimeout(timer);
	});

	const shown = $derived(complete && !rangeError ? report : null);
	const totals = $derived(shown?.totals);
	const empty = $derived(
		totals ? totals.paidCount + totals.openCount + totals.voidCount === 0 : false,
	);
	const waiting = $derived(pending && waitShown);

	function daysWord(n: number): string {
		if (n === 1) return message(language, 'reportsDay1');
		if (n === 2) return message(language, 'reportsDay2');
		return fill(message(language, n <= 10 ? 'reportsDaysFew' : 'reportsDaysMany'), n);
	}

	const shopLine = $derived(
		chosenShop === 'all'
			? message(language, 'ordersAllShops')
			: (shops.value.find((shop) => shop.id === chosenShop)?.name ?? ''),
	);

	// "Discounts given · on 2 orders": the count sits in a left-to-right
	// isolate, so it keeps its place inside Arabic.
	function discountsGiven(n: number): string {
		const key: MessageKey =
			n === 1
				? 'reportsDiscountsOn1'
				: n >= 3 && n <= 10
					? 'reportsDiscountsOnFew'
					: 'reportsDiscountsOnMany';
		return fill(message(language, key), leftToRight(String(n)));
	}

	const figures = $derived(
		totals
			? [
					{
						label: message(language, 'reportsPaid'),
						value: String(totals.paidCount),
						testId: 'report-paid',
					},
					{
						label: message(language, 'reportsNet'),
						value: money(totals.netMinor),
						testId: 'report-net',
					},
					{
						label: message(language, 'reportsAverage'),
						value: totals.paidCount ? money(totals.averageMinor) : '—',
						testId: 'report-average',
					},
					{
						label: message(language, 'reportsFees'),
						value: money(totals.deliveryFeesMinor),
						testId: 'report-fees',
					},
					{
						label: discountsGiven(totals.discountedCount),
						value: money(totals.discountsMinor),
						testId: 'report-discounts-given',
					},
				]
			: [],
	);

	const mix = $derived.by(() => {
		if (!totals) return [];
		const larger = Math.max(totals.cashMinor, totals.cardMinor);
		return (
			[
				['reportsCash', totals.cashMinor, 'report-cash'],
				['reportsCard', totals.cardMinor, 'report-card'],
			] as const
		).map(([key, value, testId]) => {
			const share = totals.netMinor ? (value / totals.netMinor) * 100 : 0;
			return {
				key,
				value: money(value),
				pct: `${Math.round(share)}%`,
				bar: share,
				dim: value < larger,
				testId,
			};
		});
	});

	const small = $derived(
		totals
			? [
					{
						key: 'reportsVoid' as MessageKey,
						value: `${totals.voidCount} · ${money(totals.voidMinor)}`,
						testId: 'report-void',
					},
					{
						key: 'reportsOpen' as MessageKey,
						value: `${totals.openCount} · ${money(totals.dueMinor)}`,
						testId: 'report-open',
					},
					{
						key: 'reportsNoSale' as MessageKey,
						value: String(totals.noSaleCount),
						testId: 'report-nosale',
					},
				]
			: [],
	);

	const typeKeys: Record<string, MessageKey> = {
		dine_in: 'sellDineIn',
		takeaway: 'sellTakeaway',
		delivery: 'sellDeliveryType',
	};

	const byType = $derived.by(() => {
		const groups = ['dine_in', 'takeaway', 'delivery']
			.map((key) => (shown?.byType ?? []).find((group) => group.key === key))
			.filter((group) => group !== undefined);
		return withBars(groups, (group) => group.netMinor);
	});

	const groupColumns = (first: MessageKey) => [
		{ label: message(language, first), w: 'minmax(0,1fr)' },
		{ label: message(language, 'reportsOrders'), w: '72px', align: 'end' as const },
		{ label: message(language, 'reportsNet'), w: '112px', align: 'end' as const },
	];

	const itemColumns = $derived([
		{ label: message(language, 'reportsItem'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'reportsQty'), w: '112px', align: 'end' as const },
		{ label: message(language, 'reportsRevenue'), w: '112px', align: 'end' as const },
	]);

	const items = $derived(shown?.items ?? []);
	const sold = $derived.by(() => {
		const list = items.filter((item) => item.qty > 0).map((item) => ({ item }));
		return withBars(list, (entry) => entry.item.qty);
	});
	const unsold = $derived(items.filter((item) => item.qty === 0));
	// Pieces and portions are two counts, never one sum (UC-19.R13); the
	// revenue beside them is one.
	const itemsQty = $derived(
		items.filter((item) => item.soldBy !== 'weight').reduce((sum, item) => sum + item.qty, 0),
	);
	const itemsPortions = $derived(
		items.filter((item) => item.soldBy === 'weight').reduce((sum, item) => sum + item.qty, 0),
	);
	const anyWeighed = $derived(items.some((item) => item.soldBy === 'weight'));
	const itemsRevenue = $derived(items.reduce((sum, item) => sum + item.revenueMinor, 0));
	function tagsOf(
		item: Item,
	): { text: string; tone: 'flag' | 'neutral' | 'quiet'; dot: boolean }[] {
		const tags: { text: string; tone: 'flag' | 'neutral' | 'quiet'; dot: boolean }[] = [];
		if (item.soldOut)
			tags.push({ text: message(language, 'reportsSoldOut'), tone: 'flag', dot: true });
		for (const shop of item.soldOutAt ?? []) {
			tags.push({
				text: fill(message(language, 'reportsSoldOutAt'), shop),
				tone: 'flag',
				dot: true,
			});
		}
		if (item.addedOn) {
			tags.push({
				text: fill(message(language, 'reportsAdded'), formatDay(item.addedOn, language)),
				tone: 'neutral',
				dot: false,
			});
		}
		if (!item.onMenu)
			tags.push({ text: message(language, 'reportsGone'), tone: 'quiet', dot: false });
		return tags;
	}

	const hours = $derived.by(() => {
		const list = [...(shown?.byHour ?? [])].sort((a, b) =>
			byTradingHour(dayStartHour)(a.hour, b.hour),
		);
		return withBars(list, (hour) => hour.paidCount);
	});

	// Discounts: the rows come in the server's order, the largest amount
	// first. No row has a bar, and no row carries a rate or a cashier's sales.
	const discounts = $derived(shown?.discounts ?? []);
	const cashiers = $derived(shown?.discountsByCashier ?? []);

	// Dine-in by table (UC-39): a report of one shop only, in the server's
	// table-name order, not by sales, each table's orders beside its sales.
	const tableRows = $derived(shown?.tables ?? []);
	const tableColumns = $derived([
		{ label: message(language, 'reportsTable'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'reportsPaid'), w: '112px', align: 'end' as const },
		{ label: message(language, 'reportsNet'), w: '120px', align: 'end' as const },
	]);

	function tableName(row: TableRow): string {
		return row.noTable ? message(language, 'reportsNoTable') : row.name;
	}

	const discountColumns = $derived([
		{ label: message(language, 'reportsDiscount'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'reportsOrders'), w: '72px', align: 'end' as const },
		{ label: message(language, 'reportsTookOff'), w: '112px', align: 'end' as const },
	]);

	const cashierColumns = $derived([
		{ label: message(language, 'reportsCashier'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'reportsPaid'), w: '96px', align: 'end' as const },
		{ label: message(language, 'reportsWithDiscount'), w: '104px', align: 'end' as const },
		{ label: message(language, 'reportsTookOff'), w: '104px', align: 'end' as const },
	]);

	function discountName(discount: Discount): string {
		return discount.managerDiscount ? message(language, 'discountManager') : discount.name;
	}
</script>

{#snippet itemRow(item: Item, bar: number, dim: boolean)}
	<Row testId="report-item-{itemRowId(item, items)}">
		<span class="named">
			<span class="tags">
				<span class="name">{item.name}</span>
				{#each tagsOf(item) as tag (tag.text)}
					<Chip text={tag.text} tone={tag.tone} size="md" dot={tag.dot} />
				{/each}
			</span>
			<Meter value={bar} {dim} label={item.name} />
		</span>
		<span class="n soft end" data-testid="report-item-qty-{itemRowId(item, items)}"
			><bdi dir="ltr">{item.qty}</bdi>{#if item.soldBy === 'weight'}&nbsp;{message(
					language,
					'reportsPortions',
				)}{/if}</span
		>
		<span class="n strong end"><bdi dir="ltr">{money(item.revenueMinor)}</bdi></span>
	</Row>
{/snippet}

<div class="reports" data-testid="panel-reports">
	<div class="choices">
		<div class="choice">
			<span class="label">{message(language, 'reportsPeriod')}</span>
			<div class="periods" role="group" aria-label={message(language, 'reportsPeriod')}>
				{#each periods as key (key)}
					<Button
						testId="report-period-{key}"
						label={message(language, periodKeys[key])}
						tone={period === key ? 'ink' : 'secondary'}
						size="sm"
						pressed={period === key}
						onPress={() => pickPeriod(key)}
					/>
				{/each}
			</div>
		</div>
		{#if !shopId}
			<div class="choice">
				<span class="label">{message(language, 'ordersShop')}</span>
				<Segmented
					label={message(language, 'ordersShop')}
					options={[
						{ value: 'all', label: message(language, 'ordersAllShops'), testId: 'report-shop-all' },
						...shops.value.map((shop) => ({
							value: shop.id,
							label: shop.name,
							testId: `report-shop-${shop.id}`,
						})),
					]}
					value={chosenShop}
					onChange={(picked) => (selectedShop = picked)}
				/>
			</div>
		{/if}
	</div>

	{#if period === 'range'}
		<div class="range">
			<div class="day">
				<Field
					testId="report-from"
					label={message(language, 'reportsFrom')}
					type="date"
					mono
					max={today}
					value={rangeFrom}
					onInput={(value) => (rangeFrom = value)}
				/>
			</div>
			<div class="day">
				<Field
					testId="report-to"
					label={message(language, 'reportsTo')}
					type="date"
					mono
					max={today}
					value={rangeTo}
					error={rangeError ? message(language, rangeError) : undefined}
					errorTestId="report-range-error"
					onInput={(value) => (rangeTo = value)}
				/>
			</div>
			<p class="hint">{message(language, 'reportsUpTo')}</p>
		</div>
	{/if}

	{#if complete && !rangeError}
		<p class="line" data-testid="report-line">
			<strong>{formatRange(range.from, range.to, language)}</strong>
			<span>{daysWord(daySpan(range.from, range.to))}</span>
			<span>{shopLine}</span>
		</p>
	{/if}

	{#if !complete}
		{#if !today}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'reportsLoading')}
					caption={message(language, 'reportsLoading')}
				/>
			</div>
		{/if}
	{:else if !rangeError}
		{#if !(empty && !waiting)}
			<div class="parts">
				<Segmented
					label={message(language, 'reportsPart')}
					options={[
						{
							value: 'overview',
							label: message(language, 'reportsOverview'),
							testId: 'report-part-overview',
						},
						{
							value: 'items',
							label: message(language, 'reportsItems'),
							testId: 'report-part-items',
						},
						{
							value: 'hours',
							label: message(language, 'reportsHours'),
							testId: 'report-part-hours',
						},
						{
							value: 'discounts',
							label: message(language, 'reportsDiscounts'),
							testId: 'report-part-discounts',
						},
						{
							value: 'tables',
							label: message(language, 'reportsTables'),
							testId: 'report-part-tables',
						},
					]}
					value={part}
					onChange={(picked) => (part = picked as Part)}
				/>
			</div>
		{/if}

		{#if waiting}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'reportsLoading')}
					caption={message(language, 'reportsLoading')}
				/>
			</div>
		{:else if !shown && !failed}
			<!-- The first read, inside its 300 ms: nothing yet, and no wait shown. -->
		{:else if failed}
			<Card pad>
				<p class="empty">{message(language, 'loadFailed')}</p>
			</Card>
		{:else if empty}
			<Card pad testId="report-empty">
				<p class="empty">{message(language, 'reportsEmpty')}</p>
			</Card>
		{:else if totals && part === 'overview'}
			<Card
				title={message(language, 'reportsTotals')}
				note={message(language, 'reportsTotalsNote')}
				testId="report-totals"
			>
				<div class="totals">
					<div class="figures">
						{#each figures as figure (figure.testId)}
							<div class="figure">
								<span class="label">{figure.label}</span>
								<span class="big" data-testid={figure.testId}
									><bdi dir="ltr">{figure.value}</bdi></span
								>
							</div>
						{/each}
					</div>
					<div class="mix">
						<span class="label">{message(language, 'reportsMix')}</span>
						{#each mix as share (share.key)}
							<div class="share">
								<div class="share-line">
									<span class="name">{message(language, share.key)}</span>
									<span class="n figures-pair"
										><bdi dir="ltr" class="strong">{share.pct}</bdi><bdi
											dir="ltr"
											class="soft"
											data-testid={share.testId}>{share.value}</bdi
										></span
									>
								</div>
								<Meter value={share.bar} dim={share.dim} label={message(language, share.key)} />
							</div>
						{/each}
					</div>
					<p class="small">
						{#each small as fact (fact.key)}
							<span class="fact"
								>{message(language, fact.key)}
								<bdi dir="ltr" class="n strong" data-testid={fact.testId}>{fact.value}</bdi></span
							>
						{/each}
					</p>
					<p class="explain">{fill(message(language, 'reportsExplain'), dayStartHour)}</p>
				</div>
			</Card>
			<TablePanel
				columns={groupColumns('reportsType')}
				title={message(language, 'reportsByType')}
				rowMin="0"
				testId="report-by-type"
			>
				{#each byType as group (group.key)}
					<Row testId="report-type-{group.key}">
						<span class="named">
							<span class="name">{message(language, typeKeys[group.key] ?? 'sellDineIn')}</span>
							<Meter
								value={group.bar}
								dim={group.dim}
								label={message(language, typeKeys[group.key] ?? 'sellDineIn')}
							/>
						</span>
						<span class="n soft end"><bdi dir="ltr">{group.paidCount}</bdi></span>
						<span class="n strong end"><bdi dir="ltr">{money(group.netMinor)}</bdi></span>
					</Row>
				{/each}
			</TablePanel>
		{:else if part === 'items'}
			<TablePanel
				columns={itemColumns}
				title={message(language, 'reportsItemsSold')}
				note={message(language, 'reportsItemsNote')}
				rowMin="0"
				testId="report-items"
			>
				{#each sold as { item, bar, dim } (itemRowKey(item))}
					{@render itemRow(item, bar, dim)}
				{/each}
				{#if unsold.length}
					<Row style="background: var(--surface-card-header)" testId="report-zero">
						<span class="whole strong">{fill(message(language, 'reportsZero'), unsold.length)}</span
						>
					</Row>
					{#each unsold as item (itemRowKey(item))}
						{@render itemRow(item, 0, true)}
					{/each}
				{/if}
				<Row style="background: var(--surface-card-header); border-block-end: 0">
					<span class="caption">{message(language, 'reportsItemsTotal')}</span>
					{#if anyWeighed}
						<span class="n strong end counts">
							<span data-testid="report-items-pieces"
								><bdi dir="ltr">{itemsQty}</bdi>&nbsp;{message(language, 'reportsPieces')}</span
							>
							<span data-testid="report-items-portions"
								><bdi dir="ltr">{itemsPortions}</bdi>&nbsp;{message(
									language,
									'reportsPortions',
								)}</span
							>
						</span>
					{:else}
						<span class="n strong end"><bdi dir="ltr">{itemsQty}</bdi></span>
					{/if}
					<span class="n strong end" data-testid="report-items-total"
						><bdi dir="ltr">{money(itemsRevenue)}</bdi></span
					>
				</Row>
			</TablePanel>
		{:else if part === 'hours'}
			<div class="hours">
				<TablePanel
					columns={groupColumns('reportsHour')}
					title={message(language, 'reportsByHour')}
					note={fill(message(language, 'reportsHoursNote'), dayStartHour)}
					rowMin="0"
					testId="report-hours"
				>
					{#each hours as hour (hour.hour)}
						{@const name = hourLabel(
							hour.hour,
							message(language, 'reportsAm'),
							message(language, 'reportsPm'),
						)}
						<Row testId="report-hour-{hour.hour}">
							<span class="named">
								<span class="name">{name}</span>
								<Meter value={hour.bar} dim={hour.dim} label={name} />
							</span>
							<span class="n soft end"><bdi dir="ltr">{hour.paidCount}</bdi></span>
							<span class="n strong end"><bdi dir="ltr">{money(hour.netMinor)}</bdi></span>
						</Row>
					{/each}
				</TablePanel>
			</div>
		{:else if totals && part === 'discounts'}
			{#if totals.discountedCount === 0}
				<!-- The list's rows at 0 say nothing in a period with no
				discount given, so neither table is drawn. -->
				<Card pad testId="report-discounts-none">
					<p class="empty">{message(language, 'reportsDiscountsNone')}</p>
				</Card>
			{:else}
				<div class="discounts">
					<TablePanel
						columns={discountColumns}
						title={message(language, 'reportsEachDiscount')}
						note={message(language, 'reportsEachDiscountNote')}
						rowMin="0"
						testId="report-discounts"
					>
						{#each discounts as discount (discount.discountId ?? 'manager')}
							<Row testId="report-discount-{discount.discountId ?? 'manager'}">
								<span class="tags cell">
									<span class="name">{discountName(discount)}</span>
									{#if discount.addedOn}
										<Chip
											text={fill(
												message(language, 'reportsAdded'),
												formatDay(discount.addedOn, language),
											)}
											tone="neutral"
											size="md"
										/>
									{/if}
									{#if discount.removed}
										<Chip text={message(language, 'reportsRemoved')} tone="quiet" size="md" />
									{/if}
								</span>
								<span class="n soft end"><bdi dir="ltr">{discount.paidCount}</bdi></span>
								<span class="n strong end"
									><bdi dir="ltr">{money(discount.discountsMinor)}</bdi></span
								>
							</Row>
						{/each}
						<Row style="background: var(--surface-card-header); border-block-end: 0">
							<span class="caption">{message(language, 'reportsDiscountsTotal')}</span>
							<span class="n strong end"><bdi dir="ltr">{totals.discountedCount}</bdi></span>
							<span class="n strong end" data-testid="report-discounts-total"
								><bdi dir="ltr">{money(totals.discountsMinor)}</bdi></span
							>
						</Row>
					</TablePanel>
					<TablePanel
						columns={cashierColumns}
						title={message(language, 'reportsByCashier')}
						note={message(language, 'reportsByCashierNote')}
						rowMin="0"
						testId="report-by-cashier"
					>
						{#each cashiers as cashier (cashier.cashierId || cashier.name)}
							<Row testId="report-cashier-{cashier.cashierId || 'unknown'}">
								<span class="cell name">{cashier.name}</span>
								<span class="n soft end"><bdi dir="ltr">{cashier.paidCount}</bdi></span>
								<span class="n soft end"><bdi dir="ltr">{cashier.discountedCount}</bdi></span>
								<span class="n strong end"
									><bdi dir="ltr">{money(cashier.discountsMinor)}</bdi></span
								>
							</Row>
						{/each}
					</TablePanel>
					<p class="explain pretty">{message(language, 'reportsCashierNote')}</p>
				</div>
			{/if}
		{:else if totals && part === 'tables'}
			{#if !shown?.shopId}
				<Card pad testId="report-tables-pick-shop">
					<p class="empty">{message(language, 'reportsTablesPickShop')}</p>
				</Card>
			{:else if tableRows.length === 0}
				<Card pad testId="report-tables-none">
					<p class="empty">{message(language, 'reportsTablesNone')}</p>
				</Card>
			{:else}
				<div class="discounts">
					<TablePanel
						columns={tableColumns}
						title={message(language, 'reportsByTable')}
						note={message(language, 'reportsByTableNote')}
						rowMin="0"
						testId="report-tables"
					>
						{#each tableRows as row (row.tableId ?? 'none')}
							<Row testId="report-table-{row.tableId ?? 'none'}">
								<span class="tags cell">
									<span class="name"><bdi>{tableName(row)}</bdi></span>
									{#if row.addedOn}
										<Chip
											text={fill(
												message(language, 'reportsAdded'),
												formatDay(row.addedOn, language),
											)}
											tone="neutral"
											size="md"
										/>
									{/if}
									{#if row.removed}
										<Chip text={message(language, 'reportsRemoved')} tone="quiet" size="md" />
									{/if}
								</span>
								<span class="n soft end"><bdi dir="ltr">{row.paidCount}</bdi></span>
								<span class="n strong end"><bdi dir="ltr">{money(row.netMinor)}</bdi></span>
							</Row>
						{/each}
					</TablePanel>
					<p class="explain pretty">
						{message(language, 'reportsTablesFair')}
						{message(language, 'reportsTablesZero')}
					</p>
				</div>
			{/if}
		{/if}
	{/if}
</div>

<style>
	.reports {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.choices {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		gap: var(--space-7);
	}

	.choice {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.periods {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
	}

	.range {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3) var(--space-5);
		align-items: flex-start;
	}

	.day {
		flex: 0 1 220px;
		min-inline-size: 0;
	}

	.hint {
		flex: 1 1 100%;
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.line {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: var(--space-2) var(--space-5);
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.line strong {
		font-size: var(--text-md);
		font-weight: 700;
		color: var(--text-strong);
	}

	.parts {
		display: flex;
		max-inline-size: 100%;
		overflow-x: auto;
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.totals {
		padding: var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.figures {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 190px), 1fr));
		gap: var(--space-7);
	}

	.figure {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.big {
		font-family: var(--font-mono);
		font-size: var(--figure-sm);
		font-weight: 700;
		line-height: var(--leading-tight);
	}

	.mix {
		max-inline-size: 560px;
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.share {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.share-line {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		justify-content: space-between;
		gap: var(--space-2) var(--space-5);
		font-size: var(--text-sm);
	}

	.figures-pair {
		display: flex;
		gap: var(--space-4);
		align-items: baseline;
	}

	.small {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2) var(--space-7);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.fact {
		white-space: nowrap;
	}

	.explain {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.named {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.tags {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2) var(--space-3);
	}

	.whole {
		grid-column: 1 / -1;
		min-inline-size: 0;
		font-size: var(--text-sm);
	}

	.caption {
		min-inline-size: 0;
		font-size: var(--text-2xs);
		color: var(--text-body);
	}

	.hours {
		max-inline-size: 720px;
	}

	.discounts {
		max-inline-size: 760px;
		display: flex;
		flex-direction: column;
		gap: var(--grid-gap);
	}

	.cell {
		min-inline-size: 0;
	}

	.pretty {
		text-wrap: pretty;
	}

	.n {
		font-family: var(--font-mono);
	}

	.name {
		font-weight: 600;
	}

	.strong {
		font-weight: 700;
		color: var(--text-strong);
	}

	.soft {
		color: var(--text-body);
	}

	.end {
		min-inline-size: 0;
		text-align: end;
	}

	/* Pieces and portions, one above the other, in the total row. */
	.counts {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
	}
</style>
